import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Inbox, AlertTriangle, Search, X } from 'lucide-react'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useTickets, useTicketCompanies } from '@/queries/tickets'
import { EmptyState } from '@/components/ui/empty-state'
import { ListSkeleton } from '@/components/ui/skeleton'
import { TicketRow } from './TicketRow'
import { displayStatusLabels, priorityLabels } from '@/lib/status'
import type { DisplayStatus, Ticket, TicketPriority } from '@/types/ticket'

// Derived statuses the queue can filter on (the API's derivedStatus). Awaiting
// User also covers a tracked ticket's pending: its proposal to close, waiting on the client.
const STATUSES: Array<{ value: string; label: string }> = (
  ['open', 'user_reply', 'in_progress', 'awaiting_user', 'on_hold', 'resolved', 'closed'] as DisplayStatus[]
).map(s => ({ value: s === 'awaiting_user' ? 'awaiting_user,pending' : s, label: displayStatusLabels[s] }))

// What the queue shows when the URL carries no status: open, user reply and
// in progress tickets, the ones waiting on us. Resolved via the API's
// `needs_attention` status, so pagination and the total both see the same set.
export const DEFAULT_QUEUE_STATUS = 'needs_attention'
const PRIORITIES: TicketPriority[] = ['high', 'normal', 'low']

const SORT_OPTIONS = [
  { value: '-lastMessageAt', label: 'Latest activity' },
  { value: '-createdAt', label: 'Newest' },
  { value: 'createdAt', label: 'Oldest' },
  { value: 'priority', label: 'Priority' },
]

/** Queue filters — mirrored into the /admin URL search params by the route. */
export interface QueueFilters {
  status?: string
  priority?: string
  assignee?: string
  companyId?: string
  q?: string
  sort?: string
}

interface AdminUser {
  _id: string
  name?: string
  email?: string
}

// V2 filterbar select (mockup `.filterbar select`): pill radius, 36px,
// hairline border — shared by every select in this filterbar.
const filterSelectClass =
  'h-[var(--etn-control-height-input)] shrink-0 rounded-etn-pill border border-etn-border-input bg-etn-surface px-4 text-etn-control text-etn-text-cell'

// V2 table header cell (mockup `th`): 36px, 11px uppercase letterspaced label.
const thClass =
  'h-9 whitespace-nowrap border-b border-[var(--etn-gray-100)] px-4 text-left text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label'

// V2 table card. overflow-x-auto rather than overflow-hidden: still clips to
// the rounded corners, but a narrow viewport scrolls a wide table instead of
// silently losing its right-hand columns.
const tableCardClass =
  'overflow-x-auto rounded-etn-card border border-etn-border-hairline bg-etn-surface shadow-etn-whisper'

export function AdminQueueView({
  filters,
  onChange,
}: {
  filters: QueueFilters
  onChange: (next: QueueFilters) => void
}) {
  const set = (patch: Partial<QueueFilters>) => onChange({ ...filters, ...patch })

  // Debounced search: local input state, push to the URL after a pause.
  const [searchText, setSearchText] = useState(filters.q ?? '')
  useEffect(() => { setSearchText(filters.q ?? '') }, [filters.q])
  useEffect(() => {
    const t = setTimeout(() => {
      const trimmed = searchText.trim()
      if ((filters.q ?? '') !== trimmed) set({ q: trimmed || undefined })
    }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText])

  const status = filters.status ?? DEFAULT_QUEUE_STATUS
  const params: Record<string, unknown> = {}
  if (status !== 'all') params.status = status
  if (filters.priority) params.priority = filters.priority
  if (filters.assignee) params.assignedTo = filters.assignee
  if (filters.companyId) params.companyId = filters.companyId
  if (filters.q) params.q = filters.q
  if (filters.sort) params.sort = filters.sort

  const { data, isLoading, error, refetch } = useTickets(params)
  const companies = useTicketCompanies()
  const admins = useQuery({
    queryKey: ['support-tickets', 'admins'],
    queryFn: () => api<{ data: AdminUser[] }>('/support-tickets/admins'),
    staleTime: 5 * 60_000,
  })

  // Rows carry only companyId — no companyName join server-side — so the
  // Company column's display name is joined client-side from the same
  // company list the filter select uses (same pattern as TrackerListView's
  // admin variant).
  const companyNames = new Map((companies.data?.data ?? []).map(c => [c._id, c.name ?? c._id]))

  const hasFilters = Boolean(filters.status || filters.priority || filters.assignee || filters.companyId || filters.q)
  const countLabel = data
    ? status === DEFAULT_QUEUE_STATUS
      ? `${data.total} ticket${data.total === 1 ? ' needs' : 's need'} attention`
      : `${data.total} ticket${data.total === 1 ? '' : 's'}`
    : ''

  return (
    <div className="flex-1 min-w-0">
      <div className="mb-4 flex items-center gap-3">
        <h1 className="text-lg font-semibold text-etn-text">Queue</h1>
        <span className="text-etn-micro text-etn-text-soft tabular-nums">
          {countLabel}
        </span>
        {hasFilters && (
          <button
            type="button"
            onClick={() => onChange({ sort: filters.sort })}
            className="inline-flex items-center gap-1 text-etn-micro text-etn-text-soft hover:text-etn-text"
          >
            <X className="h-3 w-3" /> Clear filters
          </button>
        )}
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-2.5">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-etn-text-soft" />
          <Input
            pill
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder="Search subjects…"
            aria-label="Search queue"
            className="pl-9"
          />
        </div>
        <select
          aria-label="Status"
          className={filterSelectClass}
          value={status}
          onChange={e => set({ status: e.target.value === DEFAULT_QUEUE_STATUS ? undefined : e.target.value })}
        >
          <option value={DEFAULT_QUEUE_STATUS}>Needs attention</option>
          <option value="awaiting_you">Assigned to me and awaiting me</option>
          <option value="all">All statuses</option>
          {STATUSES.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <select
          aria-label="Priority"
          className={filterSelectClass}
          value={filters.priority ?? 'all'}
          onChange={e => set({ priority: e.target.value === 'all' ? undefined : e.target.value })}
        >
          <option value="all">All priorities</option>
          {PRIORITIES.map(p => (
            <option key={p} value={p}>{priorityLabels[p]}</option>
          ))}
        </select>
        <select
          aria-label="Assignee"
          className={filterSelectClass}
          value={filters.assignee ?? ''}
          onChange={e => set({ assignee: e.target.value || undefined })}
        >
          <option value="">Anyone</option>
          <option value="me">Assigned to me</option>
          <option value="none">Unassigned</option>
          {admins.data?.data.map(a => (
            <option key={a._id} value={a._id}>{a.name ?? a.email ?? a._id}</option>
          ))}
        </select>
        <select
          aria-label="Company"
          className={`${filterSelectClass} max-w-44`}
          value={filters.companyId ?? ''}
          onChange={e => set({ companyId: e.target.value || undefined })}
        >
          <option value="">All companies</option>
          {companies.data?.data.map(c => (
            <option key={c._id} value={c._id}>{c.name ?? c._id}</option>
          ))}
        </select>
        <select
          aria-label="Sort"
          className={filterSelectClass}
          value={filters.sort ?? '-lastMessageAt'}
          onChange={e => set({ sort: e.target.value === '-lastMessageAt' ? undefined : e.target.value })}
        >
          {SORT_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {isLoading && <ListSkeleton rows={5} />}

      {error && (
        <div className="flex flex-col items-center gap-3 py-16">
          <EmptyState
            icon={<AlertTriangle />}
            title="Couldn't load the queue."
            body="Something went wrong. Please try again."
          />
          <Button size="sm" variant="outline" onClick={() => refetch()}>Retry</Button>
        </div>
      )}

      {!isLoading && !error && (data?.data.length ?? 0) === 0 && (
        hasFilters
          ? <EmptyState icon={<Inbox />} title="No tickets match." body="Try widening the filters." />
          : <EmptyState icon={<Inbox />} title="Queue is clear." body="No tickets need triage right now." />
      )}

      {!isLoading && !error && (data?.data.length ?? 0) > 0 && (
        <div className={tableCardClass}>
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th scope="col" className={thClass}>Ticket</th>
                <th scope="col" className={thClass}>From</th>
                <th scope="col" className={thClass}>Company</th>
                <th scope="col" className={thClass}>Assigned to</th>
                <th scope="col" className={thClass}>Status</th>
                <th scope="col" className={thClass}>Priority</th>
                <th scope="col" className={thClass}>SLA</th>
                <th scope="col" className={thClass}>Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-etn-border-row">
              {data!.data.map((t: Ticket) => (
                <TicketRow
                  key={t._id}
                  ticket={t}
                  relation={t.viewerRelation ?? 'admin'}
                  hrefBase="/admin/tickets"
                  companyName={companyNames.get(t.companyId) ?? 'Unknown company'}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
